import React, { useMemo, useCallback } from 'react';
import CheckboxTree from 'react-checkbox-tree';
import {
  BsCheckSquareFill, BsChevronDown, BsChevronRight, BsSquare,
} from 'react-icons/bs';
import { FaMinusSquare } from 'react-icons/fa';
import { TreeNode } from 'utils/documents';
import { FieldState } from 'final-form';
import { useFormState } from 'react-final-form';
import { Row, Col } from 'react-bootstrap';

type DocumentTreeSelectorProps = {
  documentTree: TreeNode[];
  checked: string[];
  expanded: string[];
  onCheck: ( checked: string[] ) => void;
  onExpand: ( expanded: string[] ) => void;
  fieldState: FieldState<string[]> | undefined;
  title?: string;
  mode?: 'files' | 'folders';
};

function filterFolderPaths( checkedNodes: string[] ): string[] {
  return checkedNodes.filter( ( nodeValue ) => !/^\d+$/.test( nodeValue ) );
}

function filterFoldersOnly( nodes: TreeNode[] ): TreeNode[] {
  return nodes
    .map( ( node ) => {
      if ( node.children && node.children.length > 0 ) {
        const filteredChildren = filterFoldersOnly( node.children );
        const result: TreeNode = {
          value: node.value,
          label: node.label,
        };
        if ( filteredChildren.length > 0 ) {
          result.children = filteredChildren;
        }
        return result;
      }
      return undefined;
    } )
    .filter( ( node ): node is TreeNode => node !== undefined );
}

function DocumentTreeSelector( {
  documentTree,
  checked,
  expanded,
  onCheck,
  onExpand,
  fieldState,
  title,
  mode = 'files',
}: DocumentTreeSelectorProps ) {
  const { submitFailed } = useFormState( { subscription: { submitFailed: true } } );

  const error = fieldState?.error;
  const hasError = Boolean( error && ( fieldState?.touched || submitFailed ) );

  const processedTree = useMemo( () => {
    if ( mode === 'folders' ) {
      return filterFoldersOnly( documentTree );
    }
    return documentTree;
  }, [documentTree, mode] );

  const handleCheck = useCallback( ( checkedNodes: string[] ) => {
    if ( mode === 'folders' ) {
      onCheck( filterFolderPaths( checkedNodes ) );
    } else {
      onCheck( checkedNodes );
    }
  }, [mode, onCheck] );

  return (
    <Row className="gx-1 mt-3">
      {title && (
        <Col xs={12} className="mb-2">
          <h4 className="text-secondary mb-3">{title}</h4>
        </Col>
      )}

      <Col>
        <CheckboxTree
          nodes={processedTree}
          checked={checked}
          expanded={expanded}
          onCheck={handleCheck}
          onExpand={onExpand}
          noCascade={mode === 'folders'}
          showNodeIcon={false}
          icons={{
            check: <BsCheckSquareFill size={14} />,
            uncheck: <BsSquare size={14} />,
            halfCheck: <FaMinusSquare size={14} />,
            expandClose: <BsChevronRight />,
            expandOpen: <BsChevronDown />,
          }}
        />
        {hasError && <div className="invalid-feedback d-block">{error}</div>}
      </Col>
    </Row>
  );
}

export default DocumentTreeSelector;
